import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  Users, Plus, Search, Loader2, Link as LinkIcon, Copy, 
  CheckCircle, Trash2, Edit, ExternalLink, UserCog, ClipboardList,
  Key, Eye, EyeOff, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LideresPage = () => {
  const { api, user } = useAuth();
  const [lideres, setLideres] = useState([]);
  const [empregados, setEmpregados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [senhaDialogOpen, setSenhaDialogOpen] = useState(false);
  const [liderSelecionado, setLiderSelecionado] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmaSenha, setConfirmaSenha] = useState('');
  const [salvandoSenha, setSalvandoSenha] = useState(false);
  const [formData, setFormData] = useState({
    empregado_id: '',
    setores: [],
    pode_aprovar_justificativas: true,
    pode_aprovar_atestados: true,
    pode_aprovar_banco_horas: true,
  });

  useEffect(() => {
    fetchLideres();
    fetchEmpregados();
  }, []);

  const fetchLideres = async () => {
    try {
      setLoading(true);
      const response = await api.get('/lideres');
      setLideres(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar líderes:', error);
      toast.error('Erro ao carregar líderes');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmpregados = async () => {
    try {
      const response = await api.get('/empregados');
      setEmpregados(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar empregados:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.empregado_id) {
      toast.error('Selecione um empregado para ser líder');
      return;
    }

    try {
      if (liderSelecionado) {
        await api.put(`/lideres/${liderSelecionado.id}`, formData);
        toast.success('Líder atualizado com sucesso!');
      } else {
        await api.post('/lideres', {
          ...formData,
          empresa_id: user?.empresa_id
        });
        toast.success('Líder cadastrado com sucesso!');
      }
      setDialogOpen(false);
      resetForm();
      fetchLideres();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar líder');
    }
  };

  const resetForm = () => {
    setFormData({
      empregado_id: '',
      setores: [],
      pode_aprovar_justificativas: true,
      pode_aprovar_atestados: true,
      pode_aprovar_banco_horas: true,
    });
    setLiderSelecionado(null);
  };

  const handleEdit = (lider) => {
    setLiderSelecionado(lider);
    setFormData({
      empregado_id: lider.empregado_id,
      setores: lider.setores || [],
      pode_aprovar_justificativas: lider.pode_aprovar_justificativas ?? true,
      pode_aprovar_atestados: lider.pode_aprovar_atestados ?? true,
      pode_aprovar_banco_horas: lider.pode_aprovar_banco_horas ?? true,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente remover este líder?')) return;
    try {
      await api.delete(`/lideres/${id}`);
      toast.success('Líder removido com sucesso!');
      fetchLideres();
    } catch (error) {
      toast.error('Erro ao remover líder');
    }
  };

  // Abrir modal de gerenciamento de senha
  const abrirGerenciamentoSenha = (lider) => {
    setLiderSelecionado(lider);
    setNovaSenha('');
    setConfirmaSenha('');
    setShowPassword(false);
    setSenhaDialogOpen(true);
  };

  // Gerar senha aleatória
  const gerarSenhaAleatoria = () => {
    const chars = '0123456789';
    let senha = '';
    for (let i = 0; i < 6; i++) {
      senha += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNovaSenha(senha);
    setConfirmaSenha(senha);
  };

  // Salvar nova senha do líder
  const handleSalvarSenha = async () => {
    if (!novaSenha) {
      toast.error('Digite uma senha');
      return;
    }
    if (novaSenha.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    if (novaSenha !== confirmaSenha) {
      toast.error('As senhas não conferem');
      return;
    }

    try {
      setSalvandoSenha(true);
      await api.post(`/lideres/${liderSelecionado.id}/definir-senha`, {
        senha: novaSenha
      });
      toast.success('Senha definida com sucesso!');
      setSenhaDialogOpen(false);
      fetchLideres();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao definir senha');
    } finally {
      setSalvandoSenha(false);
    }
  };

  // Gerar/Copiar link de acesso
  const gerarLinkAcesso = (lider) => {
    // O link agora aponta para a tela de login do líder (sem token na URL)
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/lider-login`;
    setGeneratedLink(link);
    setLiderSelecionado(lider);
    setLinkDialogOpen(true);
  };

  const copiarLink = () => {
    navigator.clipboard.writeText(generatedLink);
    toast.success('Link copiado para a área de transferência!');
  };

  const filteredLideres = lideres.filter(l => 
    l.empregado_nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    l.empregado_matricula?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Empregados que não são líderes ainda
  const empregadosDisponiveis = empregados.filter(e => 
    !lideres.some(l => l.empregado_id === e.id) || 
    (liderSelecionado && liderSelecionado.empregado_id === e.id)
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="lideres-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <UserCog className="w-7 h-7 text-accent" />
            Gestão de Líderes
          </h1>
          <p className="text-muted-foreground">
            Cadastre líderes, defina senhas e gere links de acesso para aprovação de justificativas
          </p>
        </div>
        <Button onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="btn-novo-lider">
          <Plus className="w-4 h-4 mr-2" />
          Novo Líder
        </Button>
      </div>

      {/* Barra de busca */}
      <Card>
        <CardContent className="pt-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Buscar por nome ou matrícula..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="input-busca-lider"
            />
          </div>
        </CardContent>
      </Card>

      {/* Tabela de Líderes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-accent" />
            Líderes Cadastrados ({filteredLideres.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-accent" />
            </div>
          ) : filteredLideres.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <UserCog className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum líder cadastrado</p>
              <p className="text-sm">Clique em &quot;Novo Líder&quot; para começar</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>CPF</TableHead>
                  <TableHead>Setor</TableHead>
                  <TableHead>Subordinados</TableHead>
                  <TableHead>Senha</TableHead>
                  <TableHead>Link de Acesso</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLideres.map((lider) => (
                  <TableRow key={lider.id} data-testid={`lider-row-${lider.id}`}>
                    <TableCell className="font-medium">{lider.empregado_nome}</TableCell>
                    <TableCell>{lider.empregado_cpf || '-'}</TableCell>
                    <TableCell>{lider.empregado_setor || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{lider.subordinados_count || 0} empregados</Badge>
                    </TableCell>
                    <TableCell>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => abrirGerenciamentoSenha(lider)}
                        className={lider.tem_senha ? "border-emerald-500 text-emerald-600" : "border-amber-500 text-amber-600"}
                        data-testid={`btn-senha-${lider.id}`}
                      >
                        <Key className="w-4 h-4 mr-1" />
                        {lider.tem_senha ? 'Alterar' : 'Definir'}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => gerarLinkAcesso(lider)}
                        data-testid={`btn-link-${lider.id}`}
                      >
                        <LinkIcon className="w-4 h-4 mr-1" />
                        Link
                      </Button>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(lider)}
                          data-testid={`btn-editar-${lider.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(lider.id)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`btn-excluir-${lider.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Informativo sobre vinculação */}
      <Card className="bg-blue-500/10 border-blue-500/30">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <ClipboardList className="w-6 h-6 text-blue-500 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-blue-700 dark:text-blue-300">Como funciona o acesso do líder?</h3>
              <ol className="text-sm text-muted-foreground mt-1 list-decimal list-inside space-y-1">
                <li>Cadastre o líder selecionando um empregado</li>
                <li>Defina a <strong>senha de acesso</strong> do líder</li>
                <li>Copie e envie o <strong>link de acesso</strong> para o líder</li>
                <li>O líder acessa o link, digita seu <strong>CPF e senha</strong> para entrar</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dialog de Cadastro/Edição */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{liderSelecionado ? 'Editar Líder' : 'Novo Líder'}</DialogTitle>
            <DialogDescription>
              {liderSelecionado 
                ? 'Atualize as informações do líder' 
                : 'Selecione um empregado para ser líder e defina suas permissões'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="empregado_id">Empregado *</Label>
              <Select
                value={formData.empregado_id}
                onValueChange={(value) => setFormData({ ...formData, empregado_id: value })}
              >
                <SelectTrigger data-testid="select-empregado-lider">
                  <SelectValue placeholder="Selecione um empregado" />
                </SelectTrigger>
                <SelectContent>
                  {empregadosDisponiveis.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.nome} - {emp.cpf || emp.matricula}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <Label>Permissões</Label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.pode_aprovar_justificativas}
                    onChange={(e) => setFormData({ ...formData, pode_aprovar_justificativas: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Aprovar Justificativas de Ponto</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.pode_aprovar_atestados}
                    onChange={(e) => setFormData({ ...formData, pode_aprovar_atestados: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Aprovar Atestados Médicos</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.pode_aprovar_banco_horas}
                    onChange={(e) => setFormData({ ...formData, pode_aprovar_banco_horas: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Aprovar Banco de Horas</span>
                </label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="btn-salvar-lider">
                {liderSelecionado ? 'Atualizar' : 'Cadastrar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de Gerenciamento de Senha */}
      <Dialog open={senhaDialogOpen} onOpenChange={setSenhaDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-500" />
              {liderSelecionado?.tem_senha ? 'Alterar Senha' : 'Definir Senha'}
            </DialogTitle>
            <DialogDescription>
              {liderSelecionado?.tem_senha 
                ? `Altere a senha de acesso de ${liderSelecionado?.empregado_nome}` 
                : `Defina a senha de acesso para ${liderSelecionado?.empregado_nome}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <span>CPF do Líder:</span>
              </div>
              <p className="font-mono font-semibold">{liderSelecionado?.empregado_cpf || 'Não informado'}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="nova-senha">Nova Senha *</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="nova-senha"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Digite a senha"
                    value={novaSenha}
                    onChange={(e) => setNovaSenha(e.target.value)}
                    className="pr-10"
                    data-testid="input-nova-senha"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={gerarSenhaAleatoria}
                  title="Gerar senha aleatória"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirma-senha">Confirmar Senha *</Label>
              <Input
                id="confirma-senha"
                type={showPassword ? 'text' : 'password'}
                placeholder="Confirme a senha"
                value={confirmaSenha}
                onChange={(e) => setConfirmaSenha(e.target.value)}
                data-testid="input-confirma-senha"
              />
            </div>

            {novaSenha && confirmaSenha && novaSenha !== confirmaSenha && (
              <p className="text-sm text-destructive">As senhas não conferem</p>
            )}

            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
              <p className="text-sm text-amber-700 dark:text-amber-300">
                <strong>Importante:</strong> O líder usará esta senha junto com seu CPF para acessar o sistema de aprovações.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setSenhaDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSalvarSenha} 
              disabled={salvandoSenha || !novaSenha || novaSenha !== confirmaSenha}
              data-testid="btn-salvar-senha"
            >
              {salvandoSenha ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Key className="w-4 h-4 mr-2" />}
              Salvar Senha
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Link de Acesso */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Link de Acesso
            </DialogTitle>
            <DialogDescription>
              Envie este link para <strong>{liderSelecionado?.empregado_nome}</strong> acessar o sistema de aprovações.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-3">
              <Label className="text-xs text-muted-foreground">Link de Acesso</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  value={generatedLink}
                  readOnly
                  className="font-mono text-sm"
                  data-testid="input-link-gerado"
                />
                <Button onClick={copiarLink} variant="outline" size="icon" data-testid="btn-copiar-link">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                <strong>Credenciais do líder:</strong>
              </p>
              <ul className="text-sm text-muted-foreground mt-1 list-disc list-inside">
                <li>CPF: <strong>{liderSelecionado?.empregado_cpf || 'Não informado'}</strong></li>
                <li>Senha: <strong>{liderSelecionado?.tem_senha ? '(definida)' : '⚠️ Não definida - defina primeiro!'}</strong></li>
              </ul>
            </div>

            {!liderSelecionado?.tem_senha && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  <strong>Atenção:</strong> Defina a senha do líder antes de enviar o link. 
                  Sem senha definida, ele não conseguirá acessar.
                </p>
              </div>
            )}

            <div className="flex gap-2">
              <Button 
                onClick={() => window.open(generatedLink, '_blank')} 
                variant="outline" 
                className="flex-1"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Testar Link
              </Button>
              <Button onClick={() => setLinkDialogOpen(false)} className="flex-1">
                Fechar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LideresPage;
